import React, { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MonthView } from '@/components/myPay/MonthView';
import { PinStatus } from '@/components/myPay/PinStatus';
import { LangToggle } from '@/components/myPay/LangToggle';
import { makeT } from '@/components/myPay/i18n';
import { currentMonth, myPayApi, myPayErr, myPayStore } from '@/services/myPayApi';

/** HR / payroll viewers: any Staff ID → the same month view staff see, with Print / PDF. */
export const HrLookup = () => {
  const [sid, setSid] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lang, setLangS] = useState(myPayStore.lang());
  const t = useMemo(() => makeT(lang), [lang]);
  const setLang = (l) => { myPayStore.setLang(l); setLangS(l); };

  const load = async (id, month) => {
    setBusy(true); setError('');
    try { setData(await myPayApi.lookup(id, month)); } catch (e) { setData(null); setError(myPayErr(e, 'Could not load this staff member')); } finally { setBusy(false); }
  };
  const search = (e) => { e.preventDefault(); if (sid.trim()) load(sid.trim(), data?.month || currentMonth()); };

  return (
    <div className="space-y-5" data-testid="hr-pay-lookup">
      <form onSubmit={search} className="flex flex-wrap items-center gap-2 print:hidden">
        <Input value={sid} onChange={(e) => setSid(e.target.value.toUpperCase())} placeholder="Staff ID" className="w-48 font-mono" data-testid="hr-pay-lookup-input" />
        <Button type="submit" disabled={!sid.trim() || busy} className="bg-[#1A1F2E] hover:bg-[#2D5F4F]" data-testid="hr-pay-lookup-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Look up
        </Button>
        <span className="text-xs text-gray-500">Current month + last 6 · same view the staff member sees</span>
        <div className="ml-auto"><LangToggle lang={lang} setLang={setLang} /></div>
      </form>
      {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="hr-pay-lookup-error">{error}</p>}
      {!data && !error && <p className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-sm text-gray-500" data-testid="hr-pay-lookup-empty">Enter a Staff ID to see their attendance and pay.</p>}
      {data?.pin && <PinStatus staff={data.staff} pin={data.pin} onChange={(pin) => setData((d) => ({ ...d, pin }))} />}
      {data && <MonthView data={data} t={t} lang={lang} busy={busy} onMonth={(m) => load(data.staff.staff_id, m)} />}
    </div>
  );
};

export default HrLookup;
